import { useEffect, useRef, useState } from 'react'
import { t } from '@/translate/t'

type TurnstileApi = {
  render: (element: HTMLElement, options: Record<string, unknown>) => string
  remove: (id: string) => void
}
const api = () => (window as Window & { turnstile?: TurnstileApi }).turnstile
let scriptPromise: Promise<TurnstileApi> | undefined

function loadTurnstile(): Promise<TurnstileApi> {
  const existing = api()
  if (existing) return Promise.resolve(existing)
  if (!scriptPromise) {
    scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
      const script = document.createElement('script')
      const timer = window.setTimeout(() => fail(), 15000)
      const fail = () => {
        window.clearTimeout(timer)
        script.remove()
        scriptPromise = undefined
        reject(new Error('verification_unavailable'))
      }
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.onerror = fail
      script.onload = () => {
        const loaded = api()
        if (!loaded) { fail(); return }
        window.clearTimeout(timer)
        resolve(loaded)
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

export function TurnstileWidget({ siteKey, action, resetKey, onToken, language = 'fr' }: {
  siteKey: string
  action: string
  resetKey: number
  onToken: (token: string) => void
  language?: string
}) {
  const container = useRef<HTMLDivElement>(null)
  const callback = useRef(onToken)
  callback.current = onToken
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let disposed = false
    let widgetId: string | undefined
    callback.current('')
    setFailed(false)
    const failure = () => {
      if (!disposed) { callback.current(''); setFailed(true) }
    }
    if (!siteKey) { failure(); return }
    loadTurnstile().then(turnstile => {
      if (disposed || !container.current) return
      widgetId = turnstile.render(container.current, {
        sitekey: siteKey, action, language, size: 'flexible',
        'response-field': false,
        callback: (token: string) => {
          if (!disposed) { setFailed(false); callback.current(token) }
        },
        'expired-callback': () => { if (!disposed) callback.current('') },
        'timeout-callback': failure,
        'error-callback': failure
      })
    }).catch(failure)
    return () => {
      disposed = true
      callback.current('')
      if (widgetId !== undefined) api()?.remove(widgetId)
    }
  }, [siteKey, action, resetKey, attempt, language])

  return (
    <div style={{ margin: '12px 0', minHeight: 65 }}>
      <div ref={container} />
      {failed && <div role="alert">
        <p>{t('verification_unavailable')}</p>
        <button type="button" onClick={() => setAttempt(value => value + 1)}>
          {t('turnstile_retry')}
        </button>
      </div>}
    </div>
  )
}
